import React, { type ReactNode } from 'react'
import { StoreProvider } from 'easy-peasy'
import { createFMPStore } from './useStore'
import type { FormStepResult } from '../types'
import type { DomainEnum } from '@strata-monorepo/shared-types/src/Domain'
import { getHomeInfoHandoff, withHomeInfoHandoff } from '../utils/homeInfoHandoff'

interface FMPStoreProviderProps {
  initialFormState?: FormStepResult
  siteDomain: DomainEnum
  children: ReactNode
}

// Module-level singleton store instance
// This persists across component mount/unmount cycles
let storeInstance: ReturnType<typeof createFMPStore> | null = null

/**
 * Get or create the singleton store instance
 * Ensures formId remains consistent throughout the application session
 */
function getStoreInstance(
  initialFormState: FormStepResult,
  siteDomain: DomainEnum,
): ReturnType<typeof createFMPStore> {
  if (!storeInstance) {
    // Read and immediately clear sessionStorage so only this call — the one
    // that creates the real singleton store — consumes the shared formId.
    // The module-level `store` export in useStore.ts also calls createFMPStore
    // at import time, so reading sessionStorage inside formModel would race it.
    const storedFormId =
      typeof window !== 'undefined' ? sessionStorage.getItem('fmpFormId') : undefined

    if (storedFormId) {
      sessionStorage.removeItem('fmpFormId')
    }

    storeInstance = createFMPStore(
      withHomeInfoHandoff(initialFormState),
      siteDomain,
      storedFormId ?? undefined,
    )

    // The lookup Strata ran before handing the shopper over, so the home details step can
    // prefill what it found and predict usage for the real home
    const handoffLookup = getHomeInfoHandoff()?.enrichCustomerResponse
    if (handoffLookup) {
      storeInstance.getActions().enrichCustomer.setEnrichCustomerResponse(handoffLookup)
    }
  }
  return storeInstance
}

/**
 * Reset the singleton store instance
 * Useful for testing or when explicitly restarting the questionnaire
 */
export function resetStoreInstance(): void {
  if (storeInstance) {
    storeInstance = null
  }
}

/**
 * Custom StoreProvider component for the find-my-plan store to allow for optional initial state
 */
export const FMPStoreProvider: React.FC<FMPStoreProviderProps> = ({
  initialFormState = {},
  siteDomain,
  children,
}) => {
  const store = getStoreInstance(initialFormState, siteDomain)

  return <StoreProvider store={store}>{children}</StoreProvider>
}

export default FMPStoreProvider
